/**
 * Drivebase overseer state from firmware `getDrivebaseSystemState` (`state` field).
 * Names and numeric values match the firmware enum.
 *
 * Notes on how we handle each state:
 * - IDLE: The drivebase is idle.
 * - EXECUTING_PYTHON: The drivebase is executing a Python program.
 * - WRITING_PROGRAM_LOG: The drivebase is writing a program log, this is a temporary state that occurs after EXECUTING_PYTHON but we need to treat this as the program is still running.
 * - DRIVING: The drivebase is driving, can occur from executing a Python program or manual drive from controller.
 * - WAITING_FOR_FIRMWARE: The drivebase is waiting for firmware
 * - DISTRIBUTING_CORE_FIRMWARE: The drivebase is distributing core firmware
 * - DISTRIBUTING_ACCESSORY_FIRMWARE: The drivebase is distributing accessory firmware
 */
export const DRIVEBASE_SYSTEM_STATE = {
  IDLE: 'idle',
  EXECUTING_PYTHON: 'executing_python',
  WRITING_PROGRAM_LOG: 'writing_program_log',
  DRIVING: 'driving',
  WAITING_FOR_FIRMWARE: 'waiting_for_firmware',
  DISTRIBUTING_CORE_FIRMWARE: 'distributing_core_firmware',
  DISTRIBUTING_ACCESSORY_FIRMWARE: 'distributing_accessory_firmware',
} as const;

export type DrivebaseSystemState = (typeof DRIVEBASE_SYSTEM_STATE)[keyof typeof DRIVEBASE_SYSTEM_STATE];

export const DRIVEBASE_SYSTEM_STATE_BY_VALUE: Readonly<Record<number, DrivebaseSystemState>> = {
  0: DRIVEBASE_SYSTEM_STATE.IDLE,
  1: DRIVEBASE_SYSTEM_STATE.EXECUTING_PYTHON,
  2: DRIVEBASE_SYSTEM_STATE.WRITING_PROGRAM_LOG,
  3: DRIVEBASE_SYSTEM_STATE.DRIVING,
  4: DRIVEBASE_SYSTEM_STATE.WAITING_FOR_FIRMWARE,
  5: DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_CORE_FIRMWARE,
  6: DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_ACCESSORY_FIRMWARE,
};

const UNKNOWN_DRIVEBASE_SYSTEM_STATE_PREFIX = '__unknown_drivebase_system_state__';

export const DRIVEBASE_PYTHON_PROGRAM_BUSY_STATES: readonly DrivebaseSystemState[] = [
  DRIVEBASE_SYSTEM_STATE.EXECUTING_PYTHON,
  DRIVEBASE_SYSTEM_STATE.WRITING_PROGRAM_LOG,
];

export const DRIVEBASE_FIRMWARE_BUSY_STATES: readonly DrivebaseSystemState[] = [
  DRIVEBASE_SYSTEM_STATE.WAITING_FOR_FIRMWARE,
  DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_CORE_FIRMWARE,
  DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_ACCESSORY_FIRMWARE,
];

const DRIVEBASE_SYSTEM_STATE_VALUES: readonly DrivebaseSystemState[] = Object.values(DRIVEBASE_SYSTEM_STATE);

export function normalizeDrivebaseSystemState(state: unknown): string | null {
  if (state == null) {
    return null;
  }

  if (typeof state === 'number' && Number.isInteger(state)) {
    return DRIVEBASE_SYSTEM_STATE_BY_VALUE[state] ?? `${UNKNOWN_DRIVEBASE_SYSTEM_STATE_PREFIX}${state}`;
  }

  if (typeof state === 'string') {
    return state;
  }

  return null;
}

export function isKnownDrivebaseSystemState(state: string | null | undefined): state is DrivebaseSystemState {
  return typeof state === 'string' && (DRIVEBASE_SYSTEM_STATE_VALUES as readonly string[]).includes(state);
}

export function isDrivebaseSystemStateIdle(state: string | null | undefined): boolean {
  return state === DRIVEBASE_SYSTEM_STATE.IDLE;
}

export function isDrivebaseSystemStateExecutingPython(state: string | null | undefined): boolean {
  return state === DRIVEBASE_SYSTEM_STATE.EXECUTING_PYTHON;
}

/** Python is running or the DU is flushing the program log after a run. */
export function isDrivebasePythonProgramBusy(state: string | null | undefined): boolean {
  return (DRIVEBASE_PYTHON_PROGRAM_BUSY_STATES as readonly string[]).includes(state ?? '');
}

export function isDrivebaseFirmwareDistributionBusy(state: string | null | undefined): boolean {
  return (DRIVEBASE_FIRMWARE_BUSY_STATES as readonly string[]).includes(state ?? '');
}

export function isDrivebaseDriving(state: string | null | undefined): boolean {
  return state === DRIVEBASE_SYSTEM_STATE.DRIVING;
}

/** Non-idle overseer state (defer connect metadata, background firmware, etc.). */
export function isDrivebaseSystemStateBusy(state: string | null | undefined): boolean {
  return state != null && !isDrivebaseSystemStateIdle(state);
}

/** Runtime states that should keep deploy/stop UI blocked. */
export function isDrivebaseRuntimeBusyForProgramActions(state: string | null | undefined): boolean {
  return isDrivebasePythonProgramBusy(state) || isDrivebaseDriving(state) || isDrivebaseFirmwareDistributionBusy(state);
}

/**
 * Runtime states where on-device slot metadata is unreliable or unsafe to query.
 * Driving and waiting-for-firmware are excluded: slot names can still be read while the DU moves.
 */
export function isDrivebaseRuntimeBusyForSlotStorageDeferral(state: string | null | undefined): boolean {
  if (isDrivebasePythonProgramBusy(state)) {
    return true;
  }

  if (state === DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_CORE_FIRMWARE || state === DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_ACCESSORY_FIRMWARE) {
    return true;
  }

  if (state == null || isDrivebaseSystemStateIdle(state) || isDrivebaseDriving(state) || state === DRIVEBASE_SYSTEM_STATE.WAITING_FOR_FIRMWARE) {
    return false;
  }

  // Unknown non-idle overseer values: defer until idle.
  return !isKnownDrivebaseSystemState(state);
}

/** Runtime states that should defer background firmware download until idle. */
export function isDrivebaseRuntimeBusyForBackgroundFirmware(state: string | null | undefined): boolean {
  return isDrivebasePythonProgramBusy(state) || isDrivebaseDriving(state) || isDrivebaseFirmwareDistributionBusy(state);
}

/** Runtime states that should defer explicit firmware actions until the DU can safely accept them. */
export function isDrivebaseRuntimeBusyForFirmwareActions(state: string | null | undefined): boolean {
  return isDrivebaseSystemStateBusy(state) && state !== DRIVEBASE_SYSTEM_STATE.WAITING_FOR_FIRMWARE;
}
